import React, { useState, useEffect, useRef, useMemo } from "react";
import { isStaffEmail } from "@utils/user_utils";
import {
  Table,
  Space,
  Popover,
  Tooltip,
  Row,
  notification,
  Select,
  Typography,
  Divider,
  Input
} from "antd";
import { audioMessage as message } from '../Common/Notifications/audioMessage';
import { TrainingAPI } from "../../api/TrainingAPI";
import { PermissionedInput, PermissionedSelect, PermissionedButton, PermissionedPopConfirm } from '../PermissionedComponents';
import { useSelector } from "react-redux";
import { AdminNotice } from "../Settings/AdminNotice";
import {useAuth0} from "@auth0/auth0-react";
import {PlayerOptions} from "@components/Common/PlayerOptions";
import { get_colors } from "@colors/general";
import "@styles/styles-Settings.css";

const { Option } = Select;

const levenshtein = (a, b) => {
  const rows = a.length + 1;
  const cols = b.length + 1;

  const dp = Array.from({ length: rows }, (_, i) =>
    Array.from({ length: cols }, (_, j) => {
      if (i === 0) return j; // distance from empty string to b[0..j]
      if (j === 0) return i; // distance from a[0..i] to empty string
      return 0;
    })
  );

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1]; // characters match, no cost
      } else {
        const deletion = dp[i - 1][j];
        const insertion = dp[i][j - 1];
        const substitution = dp[i - 1][j - 1];
        dp[i][j] = 1 + Math.min(deletion, insertion, substitution);
      }
    }
  }

  return dp[a.length][b.length];
};


export const NicknameTable = ({ missingPlayerName, players, modalMode=false}) => {
  const [nickname, setNickname] = useState(undefined);
  const [filterText, setFilterText] = useState(undefined)
  const [selectedPlayerId, setSelectedPlayerId] = useState(undefined);
  const [data, setData] = useState([]);
  const [unRecognizedNameData, setUnRecognizedNameData] = useState([]);
  const trainingAPI = new TrainingAPI();
  const playerDropdownRef = useRef();
  const [playerDropdownOpen, setOpen] = useState(false);
  const { user } = useAuth0();


  const { 
    info : {unrecognized_names_enabled, display_name: teamName },
    playersStatus
  } = useSelector(({team}) => team);
  const isStaff = isStaffEmail(user?.email);
  const colors = get_colors(teamName)
  const showUnrecognizedNames = (unrecognized_names_enabled || isStaff) && !modalMode;

  useEffect(() => {
    if (missingPlayerName) {
      setNickname(missingPlayerName)
    }
  }, [missingPlayerName]);

  const handleDelete = (alternate_id) => {
    trainingAPI
      .deleteAlternateName(alternate_id)
      .then((data) => {
        message.success("Nickname deleted");
        getNicknames();
      })
      .catch((data) => message.error("Error deleting nickname"));
  };

  const onNicknameChange = (e) => {
    setNickname(e.target.value);
  };

  const onPlayerChange = (val) => {
    setSelectedPlayerId(val);
  };

  useEffect(() => {
    getNicknames();
  }, []);

  useEffect(() => {
    if (showUnrecognizedNames) getUnrecognizedNames();
  }, [showUnrecognizedNames]);

  const getNicknames = () => {
    trainingAPI.getAlternateNames().then((data) => {
      setData(data);
    });
  };

  const getUnrecognizedNames = () => {
    trainingAPI.getUnrecognizedNames().then((data) => {

      const playerNames = players.map((e) => `${e.first_name} ${e.last_name}`)

      for (const playerData of data) {
        const nameLower = playerData.name.toLowerCase();
        const bestMatch = playerNames.reduce((best, name) => {
          const diff = levenshtein(nameLower, name.toLowerCase());
          if (diff < best.diff) return { diff, names: [name] };
          if (diff === best.diff) return { diff, names: [...best.names, name] };
          return best;
        }, { diff: Infinity, names: [] });
        playerData["match_likelihood"] = bestMatch.diff;
        playerData["closest_match"] = bestMatch.names;
      }

      setUnRecognizedNameData(data)
    });
  }

  const onSubmit = () => {
    trainingAPI
      .addAlternateName(nickname, selectedPlayerId)
      .then((data) => {
        message.success("Nickname added");
        getNicknames();
        if (showUnrecognizedNames) getUnrecognizedNames();
        setNickname(undefined);
        setSelectedPlayerId(undefined);
        notification.destroy('nickname-notification');
      })
      .catch((data) => message.error("Error adding nickname"));
  };

  const columns = [
    {
      title: "Nickname",
      dataIndex: "nickname",
      key: "nickname",
      sorter: {
        compare: (a, b) => a.nickname.localeCompare(b.nickname),
        multiple: 1,
      },
      render: (nickname, data) => {
        return <>{nickname}</>;
      },
    },
    {
      title: "Player Name",
      dataIndex: "player_id",
      key: "player_id",
      sorter: {
        compare: (a, b) => a.last_name.localeCompare(b.last_name),
        multiple: 3,
      },
      render: (player_id, data) => {
        return (
          <>
            {data.first_name} {data.last_name}
          </>
        );
      },
    },
    {
      title: "",
      dataIndex: "operation",
      width: "10%",
      render: (_, key) => (
        <PermissionedPopConfirm
          permissionCheck="DeleteNicknames"
          title="Sure to delete?"
          onConfirm={() => {
            handleDelete(key["alternate_name_id"]);
          }}
        >
          <a style={{ color: "#1890ff" }}>Delete</a>
        </PermissionedPopConfirm>
      ),
    },
  ];

  const addNicknameFromUnrecognizedNamesWithPlayerSelected = (record, closest_match) => {
    const name = record.name

    players.forEach((player) => {
      if (`${player.first_name} ${player.last_name}` == closest_match) {
        setSelectedPlayerId(player.player_id)
      }
    })

    setNickname(name);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setOpen(true);
  }

  const addNicknameFromUnrecognizedNames = (record) => {
    const name = record.name

    setSelectedPlayerId(undefined)
    setNickname(name);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setOpen(true);
  }

  const uniqueSources = useMemo(() => {
    const sourcesSet = new Set();
    let hasEmptySource = false;
    
    unRecognizedNameData.forEach((record) => {
      const validSources = (record.sources || []).filter(source => source?.trim());
      
      if (validSources.length > 0) {
        validSources.forEach(source => sourcesSet.add(source));
      } else {
        hasEmptySource = true;
      }
    });
    
    const sources = Array.from(sourcesSet).sort();
    if (hasEmptySource) {
      sources.unshift("(No Source)");
    }
    return sources;
  }, [unRecognizedNameData]);

  const unRecognizedNamesColumns = [
    {
      title: "Unrecognized Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      sorter: {
        compare: (a, b) => a.name.localeCompare(b.name),
        multiple: 1,
      },
      render: (name) => {
        return <>{name}</>;
      }
    },
    {
      title: "First seen",
      dataIndex: "created_at",
      key: "created_at",
      width: "10%",
      sorter: {
        compare: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        multiple: 1,
      },
      render: (created_at) => {
        return <>{new Date(created_at).toLocaleDateString()}</>;
      }
    },
    {
      title: "Source",
      dataIndex: "sources",
      key: "sources",
      filters: uniqueSources.map((source) => ({
        text: source,
        value: source,
      })),
      onFilter: (value, record) => {
        if (value === "(No Source)") {
          return !record.sources || 
                 !Array.isArray(record.sources) || 
                 record.sources.length === 0 ||
                 record.sources.every(s => !s || !s.trim());
        }
        return record.sources && Array.isArray(record.sources) && record.sources.includes(value);
      },
      render: (names) => {
        return <>{(names || []).join(", ")}</>;
      }
    },
    {
      title: "Match Likelihood",
      dataIndex: "match_likelihood",
      key: "match_likelihood",
      width: "10%",
      sorter: {
        compare: (a, b) => new Date(a.match_likelihood) - new Date(b.match_likelihood),
        multiple: 1,
      },
      render: (match_likelihood, record) => {
        const medium_upper_bound = 5
        let label, color;
        if (match_likelihood < 3) {
          label = "High"; color = colors.middle;
        } else if (match_likelihood < medium_upper_bound) {
          label = "Medium"; color = colors.lower;
        } else {
          label = "Low"; color = colors.upper;
        }
        const popoverContent = (
          <div>
            {(record.closest_match || []).map((match) => (
              <Row key={match} style={{ justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 4 }}>
                <span>{match}</span>
                <Typography.Link onClick={() => addNicknameFromUnrecognizedNamesWithPlayerSelected(record, match)}>
                  Add Now
                </Typography.Link>
              </Row>
            ))}
          </div>
        );
        if (match_likelihood >= medium_upper_bound) {
          return <span style={{ color }}>{label}</span>;
        }
        return (
          <Popover content={popoverContent} title="Closest Matches">
            <span style={{ color, cursor: "pointer" }}>{label}</span>
          </Popover>
        );
      }
    },
    {
      title: "Actions",
      dataIndex: undefined,
      key: undefined,
      width: "15%",
      render: (_, record) => {
        return <Typography.Link onClick={()=> addNicknameFromUnrecognizedNames(record)}>Add Nickname</Typography.Link>;
      }
    }
  ]

  return (
    <>
      <div className="settings-row">
      <Space wrap>
        <div className="injury-item">
          <PermissionedInput
            permissionCheck="AddNicknames"
            style={{ width: 200 }}
            placeholder="Nickname"
            value={nickname}
            onChange={onNicknameChange}
          />
        </div>
        <div className="injury-item" ref={playerDropdownRef}>
          <PermissionedSelect
            permissionCheck="AddNicknames"
            showSearch
            style={{ width: 200 }}
            placeholder="Player Name"
            value={selectedPlayerId}
            onChange={onPlayerChange}
            filterOption={(input, option) =>
              option.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            open={playerDropdownOpen}
            onDropdownVisibleChange={(visible) => setOpen(visible)}
          >
            {players !== null ? (
              players.map((player, i) => {
                return PlayerOptions({ player, teamName, index: i, playersStatus});
              })
            ) : (
              <></>
            )}
          </PermissionedSelect>
        </div>
        {nickname === undefined || selectedPlayerId === undefined ? (
          <Tooltip title="Enter a nickname and a player">
            <PermissionedButton 
              permissionCheck="AddNicknames"
              type="primary" 
              disabled
            >
              Add Nickname
            </PermissionedButton>
          </Tooltip>
        ) : (
          <PermissionedButton 
            permissionCheck="AddNicknames"
            type="primary" 
            onClick={onSubmit}
          >
            Add Nickname
          </PermissionedButton>
        )}
      </Space>
      </div>
      <div className="report-injury-header2">Current Nicknames</div>
      <div className="settings-row">
        <Table
          rowKey="rowId"
          columns={columns}
          size="small"
          dataSource={data}
          style={{ width: 440, maxWidth: "100%" }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30"],
            position: ["bottomRight"],
          }}
        />
      </div>

      {
        showUnrecognizedNames ? (
        <>
         <Divider  style={{ margin: "20px 0" }} />
        <div>
          <div className="report-injury-header2 settings-first-header">Unrecognized Names</div>

          {(!unrecognized_names_enabled && isStaff) && (
            <div style={{ margin: '16px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
              <AdminNotice />
            </div>
          )}

          <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 12 }}>
            This is a list of names that have been found in the third party data that were not found on your Whistle Performance Roster.
            If these are alternate names for existing players, you can create a nickname to start tracking them in the data.
            If these are new players, you can add them to your roster in the Roster page.
          </Typography.Paragraph>

          <Row>
            <Input 
                placeholder="Start typing to filter names..." 
                value={filterText}
                onChange={(e) => {setFilterText(e.target.value);}}
            />
          </Row>

          <div className="settings-row">
            <Table
              columns={unRecognizedNamesColumns}
              size="small"
              dataSource={filterText ? unRecognizedNameData.filter((e) => e.name.toLowerCase().includes(filterText)) : unRecognizedNameData}
            />
          </div>
        </div>
        </>) : null
      }
    </>
  );
};
